import { useMemo, useRef, useState } from "react";

import { Spinner } from "src/core/components/custom-ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/core/components/ui/tooltip";

type CustomerCompaniesCellProps = {
  companyNames: string[];
  isLoading: boolean;
};

/*
 * Ce composant affiche la société principale associée au dossier, ainsi qu'un indicateur
 * permettant de consulter les autres sociétés dans une infobulle.
 */
export const CustomerCompaniesCell = ({ companyNames, isLoading }: CustomerCompaniesCellProps) => {
  const primaryCompanyName = companyNames[0] ?? "";

  const remainingCompanyNames = useMemo(() => {
    return [...companyNames.slice(1)].sort((a, b) =>
      a.localeCompare(b, "fr", { sensitivity: "base" })
    );
  }, [companyNames]);

  const primaryNameRef = useRef<HTMLSpanElement | null>(null);
  const [isPrimaryNameTruncated, setIsPrimaryNameTruncated] = useState(false);

  const handlePrimaryNameHover = () => {
    const element = primaryNameRef.current;

    if (!element) return;

    setIsPrimaryNameTruncated(element.scrollWidth > element.clientWidth);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-start">
        <Spinner size="sm" className="text-muted-foreground" />
      </div>
    );
  }

  if (!primaryCompanyName) {
    return <span>-</span>;
  }

  const primaryNameNode = (
    <span
      ref={primaryNameRef}
      className="truncate max-w-[120px]"
      onMouseEnter={handlePrimaryNameHover}
    >
      {primaryCompanyName}
    </span>
  );

  return (
    <TooltipProvider>
      <div className="flex items-center justify-start gap-x-20 min-w-0">
        {isPrimaryNameTruncated ? (
          <Tooltip>
            <TooltipTrigger asChild>{primaryNameNode}</TooltipTrigger>
            <TooltipContent side="bottom" align="start">
              <span className="max-w-[320px] break-words text-sm">{primaryCompanyName}</span>
            </TooltipContent>
          </Tooltip>
        ) : (
          primaryNameNode
        )}

        {remainingCompanyNames.length > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="flex items-center justify-center rounded-full border border-foreground text-foreground text-xs px-5 hover:cursor-pointer hover:border-blue-info hover:text-blue-info">
                +{remainingCompanyNames.length}
              </span>
            </TooltipTrigger>

            <TooltipContent side="bottom" align="end" className="max-w-[320px] p-0">
              <div className="flex flex-col gap-y-2 text-sm p-10">
                {remainingCompanyNames.map((name) => (
                  <span key={name}>{name}</span>
                ))}
              </div>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};
